import { Cookies } from './api';

/**
 * Cookie points, drawn.
 *
 * Colour carries the category, but never alone: each total is also labelled and
 * each cookie has a text alternative, so the display still works in greyscale,
 * for a colour-blind reader and for a screen reader. That rule is set elsewhere
 * in this app (FR-069) and a rewards panel is not an excuse to break it.
 *
 * The jar caps at a sensible number of drawn cookies and then says "+N" rather
 * than rendering hundreds of SVGs — a wall of biscuits stops being readable
 * long before it stops being expensive.
 */

const MAX_DRAWN = 24;

/** One cookie. `tone` selects the palette; the shape is identical either way. */
function Cookie({ tone, size = 34 }: { tone: 'dispute' | 'question'; size?: number }) {
  const fill = tone === 'dispute' ? 'var(--cookie-dispute)' : 'var(--cookie-question)';
  const chip = tone === 'dispute' ? 'var(--cookie-dispute-chip)' : 'var(--cookie-question-chip)';
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" role="presentation" focusable="false"
         className="cookie">
      <circle cx="20" cy="20" r="18" fill={fill} stroke="var(--cookie-edge)" strokeWidth="1.5" />
      {/* Chips placed by hand rather than randomly, so the icon is stable
          between renders and does not shimmer as the list updates. */}
      <circle cx="13" cy="14" r="3.1" fill={chip} />
      <circle cx="27" cy="16" r="2.6" fill={chip} />
      <circle cx="19" cy="24" r="3.4" fill={chip} />
      <circle cx="28" cy="27" r="2.2" fill={chip} />
      <circle cx="11" cy="26" r="2.1" fill={chip} />
    </svg>
  );
}

export function CookieJar({ cookies }: { cookies: Cookies }) {
  const { total, byReason } = cookies;
  const drawn: ('dispute' | 'question')[] = [
    ...Array(Math.min(byReason.disputesAccepted, MAX_DRAWN)).fill('dispute'),
    ...Array(Math.min(byReason.contributionsAccepted, Math.max(0, MAX_DRAWN - byReason.disputesAccepted))).fill('question'),
  ];
  const hidden = total - drawn.length;

  return (
    <section className="cookie-jar" aria-labelledby="cookie-heading">
      <h2 id="cookie-heading" style={{ marginTop: 0 }}>Your cookies</h2>

      <p className="cookie-total">
        <strong>{total}</strong> cookie{total === 1 ? '' : 's'} earned
      </p>

      {total === 0 ? (
        <p className="muted">
          You have not earned any cookies yet. Report a question whose answer looks wrong, or write
          a question of your own — cookies arrive when an editor accepts your work.
        </p>
      ) : (
        <>
          <div className="cookie-grid" role="img"
               aria-label={`${total} cookies: ${byReason.disputesAccepted} from accepted reports, ${byReason.contributionsAccepted} from accepted questions.`}>
            {drawn.map((tone, i) => <Cookie key={i} tone={tone} />)}
            {hidden > 0 && <span className="cookie-more" aria-hidden="true">+{hidden}</span>}
          </div>

          <ul className="cookie-legend">
            <li>
              <Cookie tone="dispute" size={20} />
              <span><strong>{byReason.disputesAccepted}</strong> from accepted question reports</span>
            </li>
            <li>
              <Cookie tone="question" size={20} />
              <span><strong>{byReason.contributionsAccepted}</strong> from accepted questions you wrote</span>
            </li>
          </ul>
        </>
      )}

      <p className="muted" style={{ marginBottom: 0 }}>
        Cookies are awarded when an editor accepts your report or publishes your question — not when
        you submit. Quality counts; volume does not.
      </p>
    </section>
  );
}
